import {
    BaseScoreResult,
    QuestionnaireResponse,
    getAnswerCode,
    findItemByLinkId
} from './types';

export enum ChronoType {
    DEFINITELY_EVENING = "Definitivamente vespertino",
    MODERATE_EVENING = "Vespertino moderado",
    INTERMEDIATE = "Intermediário",
    MODERATE_MORNING = "Matutino moderado",
    DEFINITELY_MORNING = "Definitivamente matutino"
}

export interface LightTherapyTime {
    score: number;
    recommendedTime: string;
}

export interface SleepSchedule {
    sleepStart: string;
    wakeUp: string;
}

export interface MeqSaScoreResult extends BaseScoreResult {
    totalScore: number;
    chronoType: ChronoType;
    sleepSchedule: SleepSchedule;
    lightTherapyTime: string;
    details: Record<string, number>;
}


export function processMeqSaQuestionnaire(response: QuestionnaireResponse): MeqSaScoreResult {
    const rootItems = response.item || [];

    const scoringLinkIds = [
        'horario-acordar',
        'horario-deitar',
        'depende-de-alarme',
        'facil-acordar',
        'alerta-acordar',
        'fome-acordar',
        'cansado-acordar',
        'horario-deitar-sem-compromissos',
        'desempenho-fisico',
        'cansado-sono',
        'horario-teste-mental',
        'cansado-sono-23h',
        'acordar-manha-seguinte',
        'plantao-04-06h',
        'horario-atividade-fisica',
        'desempenho-fisico-22-23h',
        'horario-trabalho-5h',
        'horario-melhor',
        'tipo-cronobiologico'
    ];

    const details: Record<string, number> = {};

    scoringLinkIds.forEach(linkId => {
        const item = findItemByLinkId(rootItems, linkId);
        const code = getAnswerCode(item);

        if (code !== undefined) {
            const answerOptions = item?.answerOption || [];
            const selectedOption = answerOptions.find(
                option => option.valueCoding?.code === code
            );

            const scoreExtension = selectedOption?.extension?.find(
                ext => ext.url === "http://hl7.org/fhir/StructureDefinition/questionnaire-optionScore"
            );

            const score = scoreExtension?.valueInteger || parseInt(code, 10);
            details[linkId] = isNaN(score) ? 0 : score;
        } else {
            details[linkId] = 0;
        }
    });

    const totalScore = Object.values(details).reduce((sum, score) => sum + score, 0);

    let chronoType: ChronoType;
    if (totalScore >= 70) {
        chronoType = ChronoType.DEFINITELY_MORNING;
    } else if (totalScore >= 59) {
        chronoType = ChronoType.MODERATE_MORNING;
    } else if (totalScore >= 42) {
        chronoType = ChronoType.INTERMEDIATE;
    } else if (totalScore >= 31) {
        chronoType = ChronoType.MODERATE_EVENING;
    } else {
        chronoType = ChronoType.DEFINITELY_EVENING;
    }

    let sleepSchedule: SleepSchedule;
    if (totalScore >= 70) {
        sleepSchedule = { sleepStart: "21:00-21:30", wakeUp: "04:00-05:00" };
    } else if (totalScore >= 59) {
        sleepSchedule = { sleepStart: "21:30-22:45", wakeUp: "05:00-06:30" };
    } else if (totalScore >= 42) {
        sleepSchedule = { sleepStart: "22:45-00:45", wakeUp: "06:30-08:30" };
    } else if (totalScore >= 31) {
        sleepSchedule = { sleepStart: "00:45-02:00", wakeUp: "08:30-10:00" };
    } else {
        sleepSchedule = { sleepStart: "02:00-03:00", wakeUp: "10:00-11:30" };
    }

    const lightTherapyTimes: LightTherapyTime[] = [
        { score: 23, recommendedTime: "08:15" },
        { score: 27, recommendedTime: "08:00" },
        { score: 31, recommendedTime: "07:45" },
        { score: 35, recommendedTime: "07:30" },
        { score: 39, recommendedTime: "07:15" },
        { score: 42, recommendedTime: "07:00" },
        { score: 46, recommendedTime: "06:45" },
        { score: 50, recommendedTime: "06:30" },
        { score: 54, recommendedTime: "06:15" },
        { score: 58, recommendedTime: "06:00" },
        { score: 62, recommendedTime: "05:45" },
        { score: 66, recommendedTime: "05:30" },
        { score: 69, recommendedTime: "05:15" },
        { score: 73, recommendedTime: "05:00" },
        { score: 77, recommendedTime: "04:45" },
    ];

    let lightTherapyTime = "07:00";

    for (let i = lightTherapyTimes.length - 1; i >= 0; i--) {
        if (totalScore >= lightTherapyTimes[i].score) {
            lightTherapyTime = lightTherapyTimes[i].recommendedTime;
            break;
        }
    }

    return {
        questionnaireId: response.questionnaire?.split('/')?.[1],
        questionnaireResponseId: response.id,
        totalScore,
        chronoType,
        sleepSchedule,
        lightTherapyTime,
        details
    };
}
